/**
 * Wallet QR / photo helpers for the Wallet Check screen.
 *
 * Decodes a QR code (live camera or uploaded image) via html5-qrcode and
 * extracts a crypto address from common payment-URI formats. Address
 * validation reuses validateWalletAddress from cryptoWalletUtils so the
 * rules stay identical to Wallet Management.
 */
import { Html5Qrcode } from "html5-qrcode";
import { validateWalletAddress, CRYPTO_NETWORKS } from "./cryptoWalletUtils";

/** EVM networks share the same 0x address shape. */
const EVM_NETWORKS = ["POLYGON", "BSC", "ETHEREUM"];

const EVM_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;

/** EIP-681 @chainId → network label used in Wallet Management. */
const CHAIN_ID_TO_NETWORK = {
  1: "ETHEREUM",
  56: "BSC",
  137: "POLYGON",
};

/**
 * Normalize an address for comparison (EVM addresses are case-insensitive).
 * Mirrors the logic used in AddWalletModal so "match / no match" is reliable.
 */
export const normalizeAddressForCompare = (address, network) => {
  const trimmed = (address || "").trim();
  if (!trimmed) return "";
  if (EVM_NETWORKS.includes(network)) return trimmed.toLowerCase();
  return trimmed;
};

const isEvmAddress = (value) => EVM_ADDRESS_RE.test((value || "").trim());

/**
 * Extract a wallet address from decoded QR text or pasted payment URI.
 *
 * Handles:
 * - EIP-681 token receive (Coinbase, etc.):
 *   ethereum:0xUSDT_CONTRACT/transfer?address=0xRECIPIENT
 * - EIP-681 direct: ethereum:0xRECIPIENT@137?value=...
 * - Plain 0x / T / bc1 addresses
 * - bitcoin: / tron: schemes
 */
export const extractAddressFromQrText = (rawText) => {
  if (!rawText || typeof rawText !== "string") return "";
  const original = rawText.trim();

  // Recipient in query string (EIP-681 token transfer — must win over contract address)
  const queryParamMatch = original.match(/[?&](?:address|recipient)=([^&]+)/i);
  if (queryParamMatch) {
    const candidate = decodeURIComponent(queryParamMatch[1].trim());
    if (isEvmAddress(candidate)) return candidate;
  }

  let text = original;

  // Strip URI scheme (ethereum:, bitcoin:, tron:, …)
  const schemeMatch = text.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:(.*)$/);
  if (schemeMatch) text = schemeMatch[1];

  text = text.replace(/^\/+/, "").trim();

  // Drop /transfer, /send, etc. — recipient is in query or was the @-segment target
  if (text.includes("/")) {
    text = text.split("/")[0];
  }

  // Primary target before query / chain id suffix
  const primary = text.split("?")[0].split("&")[0].split("@")[0].trim();

  if (isEvmAddress(primary)) return primary;
  if (/^T[a-zA-Z0-9]{33}$/.test(primary)) return primary;
  if (/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(primary)) return primary;

  // Fallback: multiple 0x in one URI — last one is usually the recipient wallet
  const allEvm = original.match(/0x[a-fA-F0-9]{40}/g);
  if (allEvm?.length === 1) return allEvm[0];
  if (allEvm?.length > 1) return allEvm[allEvm.length - 1];

  return primary;
};

/**
 * Guess network from QR URI (scheme / @chainId) or address shape.
 */
export const detectNetworkFromQrText = (rawText, address) => {
  const text = (rawText || "").trim().toLowerCase();

  if (text.startsWith("tron:")) return "TRON";
  if (text.startsWith("bitcoin:")) return "BITCOIN";

  if (text.startsWith("ethereum:")) {
    const chainMatch = (rawText || "").match(/@(1|56|137)(?:[/?&#]|$)/);
    if (chainMatch) return CHAIN_ID_TO_NETWORK[Number(chainMatch[1])] || "ETHEREUM";
    return "ETHEREUM";
  }

  return detectNetworkFromAddress(address);
};

/**
 * Guess the network from an address shape. Returns "" when unknown.
 * EVM defaults to POLYGON (project primary) when scheme is not present.
 */
export const detectNetworkFromAddress = (address) => {
  const trimmed = (address || "").trim();
  if (!trimmed) return "";
  if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) return "POLYGON";
  if (/^T[a-zA-Z0-9]{33}$/.test(trimmed)) return "TRON";
  if (/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(trimmed)) return "BITCOIN";
  return "";
};

/**
 * True when pasted/scanned text looks like a payment URI, not a bare address.
 */
export const looksLikePaymentUri = (text) => {
  const t = (text || "").trim();
  if (!t) return false;
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(t) || /[?&](?:address|recipient)=0x/i.test(t);
};

/**
 * Parse decoded QR text into { address, network, valid, raw }.
 * Validates against the detected network using the shared validator.
 */
export const parseWalletFromQr = (rawText) => {
  const raw = (rawText || "").trim();
  const address = extractAddressFromQrText(raw);
  const network = detectNetworkFromQrText(raw, address);
  const result = network
    ? validateWalletAddress(address, network)
    : { valid: false, message: "Could not recognize a wallet address" };
  return {
    address,
    network: network || "",
    valid: !!result.valid,
    message: result.message || "",
    raw,
  };
};

/**
 * Decode a QR code from an uploaded image File.
 * Returns the parsed wallet object, or throws when no QR is found.
 */
export const decodeQrFromFile = async (file) => {
  const elementId = `wallet-qr-file-${Date.now()}`;
  const holder = document.createElement("div");
  holder.id = elementId;
  holder.style.display = "none";
  document.body.appendChild(holder);

  const scanner = new Html5Qrcode(elementId, { verbose: false });
  try {
    const decodedText = await scanner.scanFile(file, false);
    return parseWalletFromQr(decodedText);
  } finally {
    try {
      await scanner.clear();
    } catch (_) {
      /* noop */
    }
    if (holder.parentElement) holder.parentElement.removeChild(holder);
  }
};

export { CRYPTO_NETWORKS };
